import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router";
import { AuthProvider, AuthGuard } from "@/lib/auth";
import { AppLayout } from "@/components/layout/AppLayout";
import { LoginPage } from "@/pages/Login";
import { ForgotPasswordPage } from "@/pages/ForgotPassword";
import { DashboardPage } from "@/pages/Dashboard";
import { ClientListPage } from "@/pages/clients/ClientList";
import { ClientDetailPage } from "@/pages/clients/ClientDetail";
import { NotFoundPage } from "@/pages/NotFound";
import { IntakeWizard } from "@/features/intake/IntakeWizard";
import { CaseManagementPage } from "@/pages/cases/CaseManagementPage";
import { CaseDetailPage } from "@/pages/cases/CaseDetailPage";
import { GLOBAL_SEARCH_ID } from "@/features/search/GlobalSearchBar";
import { ProgramsList } from "@/features/programs/ProgramsList";
import { StaffList } from "@/features/programs/StaffList";
import { SitesList } from "@/features/programs/SitesList";
import { WaitingListPage } from "@/pages/shelter/WaitingListPage";
import { MealTrackingPage } from "@/pages/shelter/MealTrackingPage";
import { TurnawayLogPage } from "@/pages/shelter/TurnawayLogPage";
import { LegalPage } from "@/pages/legal/LegalPage";
import { CourtsJudgesAdmin } from "@/features/legal/CourtsJudgesAdmin";
import { ShelterPage } from "@/pages/shelter/ShelterPage";
import { AlertsPage } from "@/features/alerts/AlertsPage";
import { AbuserSearch } from "@/features/abusers/AbuserSearch";
import { SexualAssaultPage } from "@/pages/sexual-assault/SexualAssaultPage";
import { ReportsPage } from "@/pages/reports/ReportsPage";
import { VolunteersPage } from "@/pages/volunteers/VolunteersPage";
import { EventsPage } from "@/pages/events/EventsPage";
import { AdminPage } from "@/pages/admin/AdminPage";
import { SettingsPage } from "@/pages/admin/SettingsPage";
import { MessagingPage } from "@/pages/messaging/MessagingPage";

/**
 * Global keyboard shortcut: Ctrl+K / Cmd+K to focus the search bar.
 */
function useGlobalSearchShortcut() {
  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        const searchInput = document.getElementById(GLOBAL_SEARCH_ID);
        if (searchInput) {
          searchInput.focus();
        }
      }
    };
    document.addEventListener("keydown", handler);
    return () => document.removeEventListener("keydown", handler);
  }, []);
}

export function App() {
  useGlobalSearchShortcut();

  return (
    <AuthProvider>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        <Route path="/forgot-password" element={<ForgotPasswordPage />} />

        {/* Protected routes with layout */}
        <Route
          element={
            <AuthGuard>
              <AppLayout />
            </AuthGuard>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route path="/clients" element={<ClientListPage />} />
          <Route path="/clients/new" element={<IntakeWizard />} />
          <Route path="/clients/:id" element={<ClientDetailPage />} />
          <Route path="/cases" element={<CaseManagementPage />} />
          <Route path="/cases/:id" element={<CaseDetailPage />} />
          <Route path="/shelter" element={<ShelterPage />} />
          <Route path="/shelter/waiting-list" element={<WaitingListPage />} />
          <Route path="/shelter/meals" element={<MealTrackingPage />} />
          <Route path="/shelter/turnaways" element={<TurnawayLogPage />} />
          <Route path="/alerts" element={<AlertsPage />} />
          <Route path="/abusers/search" element={<AbuserSearch />} />
          <Route path="/legal" element={<LegalPage />} />
          <Route path="/legal/courts" element={<CourtsJudgesAdmin />} />
          <Route path="/sexual-assault" element={<SexualAssaultPage />} />
          <Route path="/reports" element={<ReportsPage />} />
          <Route path="/volunteers" element={<VolunteersPage />} />
          <Route path="/events" element={<EventsPage />} />
          <Route path="/admin" element={<AdminPage />} />
          <Route path="/admin/programs" element={<ProgramsList />} />
          <Route path="/admin/staff" element={<StaffList />} />
          <Route path="/admin/sites" element={<SitesList />} />
          <Route path="/admin/users" element={<AdminPage />} />
          <Route path="/admin/agencies" element={<AdminPage />} />
          <Route path="/admin/config" element={<AdminPage />} />
          <Route path="/messaging" element={<MessagingPage />} />
          <Route path="/settings" element={<SettingsPage />} />
        </Route>

        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </AuthProvider>
  );
}
